import { createContext, useContext, useEffect, useRef, useState } from "react";
import Me from "../services/Authentification/Me";
import MeOrganizationResponse from "../services/Authentification/MeOrganizationResponse";
import msalService from "../services/Authentification/msal-service";
import userService from "../services/Authentification/UserService";
import { clearToken, getToken, setToken } from "../services/Authentification/TokenStore";

export interface ISessionContext {
  initialize: (orgId: string) => void;
  initialized: boolean;
  initStage: number;

  login: () => Promise<void>;
  signup: () => Promise<void>;
  logout: (redirectUrl: string) => Promise<void>;

  consentGiven: boolean;
  consent: () => void;

  organization: MeOrganizationResponse | undefined;
  setOrganization: (organization: MeOrganizationResponse | undefined) => void;

  user: Me | undefined;
  setUser: (me: Me | undefined) => void;
}
const defaultState = {
  initialize: (orgId: string) => {},
  initialized: false,
  initStage: 0,

  login: () => {
    return Promise.resolve();
  },
  signup: () => {
    return Promise.resolve();
  },
  logout: (redirectUrl: string) => {
    return Promise.resolve();
  },

  consentGiven: false,
  consent: () => {},

  organization: undefined,
  setOrganization: () => {},

  user: undefined,
  setUser: () => {},
};

const SessionContext = createContext<ISessionContext>(defaultState);

export const useSessionContext = () => useContext(SessionContext);

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`SessionContext: localStorage.getItem('${key}') failed`, error);
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`SessionContext: localStorage.setItem('${key}') failed`, error);
  }
};

type Props = {
  children?: JSX.Element;
};
function SessionProvider({ children }: Props) {
  const [initialized, setInitialized] = useState<boolean>(false);
  const [initStage, setInitStage] = useState<number>(0);
  const [msalAccountId, setMsalAccountId] = useState<string | undefined>(undefined);

  const [organization, setOrganization] = useState<MeOrganizationResponse | undefined>(undefined);
  const [user, setUser] = useState<Me | undefined>(undefined);

  const [consentGiven, setConsentGiven] = useState<boolean>(false);
  const internalInitialized = useRef(false);
  const lastProcessedMsalAccountId = useRef<string | undefined>();

  useEffect(() => {
    console.log("SessionContext => initialized changed", initialized);
  }, [initialized]);

  const consent = () => {
    setConsentGiven(true);
    safeLocalStorageSet("consent", "consent-1.0");
  };

  const initialize = async (orgId: string): Promise<void> => {
    const org = await userService.getMeOrganization(orgId);
    setOrganization(org);
    setInitStage(1);
    await msalService.initialize(onMsalCallback);
    setInitStage(2);
    await onMsalCallback();
  };

  const onMsalCallback = async () => {
    const msalAccount = msalService.getAccount();
    try {
      if (!msalAccount) {
        setInitialized(true);
        return;
      }

      const existingToken = getToken();
      const accountChanged = lastProcessedMsalAccountId.current !== msalAccount.localAccountId;
      const shouldFetchToken = !existingToken || !user || accountChanged;
      lastProcessedMsalAccountId.current = msalAccount.localAccountId;

      if (!msalAccountId) {
        setMsalAccountId(msalAccount.localAccountId);
      }

      if (shouldFetchToken) {
        const token = await msalService.getToken(msalAccount);
        setToken(token);
        // console.log("token", token);

        if (token) {
          try {
            const me = await userService.getMe();
            setUser(me);
          } catch (error) {
            console.error("SessionContext.onMsalCallback getMe error", error);
          }
        }
      }

      setInitialized(true);
    } catch (error) {
      console.error("SessionContext.onMsalCallback() error", error);
      setInitialized(true);
    }
  };

  const login = async (): Promise<void> => {
    await msalService.login();
  };

  const signup = async (): Promise<void> => {
    await msalService.signup();
  };

  const logout = async (redirectUrl: string): Promise<void> => {
    clearToken();
    lastProcessedMsalAccountId.current = undefined;
    await msalService.logout(redirectUrl);
    setUser(undefined);
    setMsalAccountId(undefined);
  };

  useEffect(() => {
    const init = async () => {
      //logger.logTrace("SessionProvider", "useEffect");
      try {
        const consentValue = safeLocalStorageGet("consent");
        if (consentValue === "consent-1.0") {
          setConsentGiven(true);
        }
      } finally {
        setInitialized(true);
      }
    };

    if (!internalInitialized.current) {
      internalInitialized.current = true;
      init();
    }
  }, []);

  useEffect(() => {
    if (organization) {
      if (organization.organization.authentificationless) {
        setConsentGiven(true);
      }
    }
  }, [organization]);

  return (
    <SessionContext.Provider
      value={{
        initialize,
        initialized,
        initStage,

        login,
        signup,
        logout,

        consentGiven,
        consent,

        organization,
        setOrganization,

        user,
        setUser,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export { SessionProvider };
